import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import { cn } from '../../../../../lib/utils';
import UserTableRow from './UserTableRow';
import UserTableRowSkeleton from './UserTableRowSkeleton';

const TABLE_COLUMNS = [
  { id: 'avatar', name: '', width: '50px', sortable: false, align: 'center' },
  { id: 'lastName', name: 'Last Name', width: '120px', sortable: true, align: 'left' },
  { id: 'firstName', name: 'First Name', width: '120px', sortable: true, align: 'left' },
  { id: 'email', name: 'Email', width: '200px', sortable: true, align: 'left' },
  { id: 'departmentId', name: 'Department', width: '120px', sortable: true, align: 'left' },
  { id: 'levelKey', name: 'Level', width: '80px', sortable: true, align: 'center' },
  { id: 'goalKey', name: 'Goal', width: '80px', sortable: true, align: 'center' },
  { id: 'isAdmin', name: 'Admin', width: '70px', sortable: true, align: 'center' },
  { id: 'englishLevel', name: 'English', width: '80px', sortable: true, align: 'center' },
  { id: 'createdAt', name: 'Created', width: '100px', sortable: true, align: 'left' },
  { id: 'lastLoginAt', name: 'Last Login', width: '100px', sortable: true, align: 'left' },
  { id: 'actions', name: '', width: '100px', sortable: false, align: 'right' },
];

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

function UsersTable({
  users,
  departments,
  isLoading,
  totalCount,
  currentPage,
  rowsPerPage,
  sortConfig,
  onSort,
  onPageChange,
  onRowsPerPageChange,
  onUserUpdate,
}) {
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const startItem = currentPage * rowsPerPage + 1;
  const endItem = Math.min((currentPage + 1) * rowsPerPage, totalCount);

  const handleSaveSuccess = () => {
    setSuccessMessage('User updated successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
    onUserUpdate();
  };

  const handleError = (message) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(''), 3000);
  };

  return (
    <div className="w-full">
      {/* Success Message */}
      {successMessage && (
        <div className="mx-4 mt-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-600 text-sm">
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="mx-4 mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-600 text-sm">
          {errorMessage}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              {TABLE_COLUMNS.map((column) => (
                <th
                  key={column.id}
                  className={cn(
                    'px-3 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider',
                    column.align === 'center' && 'text-center',
                    column.align === 'right' && 'text-right',
                    column.align === 'left' && 'text-left',
                  )}
                  style={{ width: column.width, minWidth: column.width }}
                >
                  {column.sortable ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                      onClick={() => onSort(column.id)}
                    >
                      {column.name}
                      <ArrowUpDown
                        className={cn('h-3 w-3', sortConfig.key === column.id && 'text-primary')}
                      />
                    </button>
                  ) : (
                    column.name
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? Array.from({ length: 5 }, (_, index) => <UserTableRowSkeleton key={index} />)
              : users.map((user) => (
                  <UserTableRow
                    key={user._id}
                    user={user}
                    departments={departments}
                    onSaveSuccess={handleSaveSuccess}
                    onError={handleError}
                  />
                ))}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {!isLoading && users.length === 0 && (
        <div className="py-12 text-center text-muted-foreground">
          No users found. Try adjusting your filters.
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-border">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows per page:</span>
            <Select
              value={String(rowsPerPage)}
              displayValue={String(rowsPerPage)}
              onValueChange={(value) => onRowsPerPageChange(Number(value))}
            >
              <SelectTrigger className="w-16 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROWS_PER_PAGE_OPTIONS.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <span className="text-sm text-muted-foreground">
            {totalCount > 0 ? `${startItem}-${endItem} of ${totalCount}` : '0 results'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground px-2">
            Page {currentPage + 1} of {totalPages || 1}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages - 1}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

UsersTable.propTypes = {
  users: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
  ).isRequired,
  departments: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      levels: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string.isRequired,
          key: PropTypes.string.isRequired,
          position: PropTypes.number.isRequired,
        }),
      ).isRequired,
    }),
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  totalCount: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  sortConfig: PropTypes.shape({
    key: PropTypes.string.isRequired,
    direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
  }).isRequired,
  onSort: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onUserUpdate: PropTypes.func.isRequired,
};

export default UsersTable;
